import mixpanel from "mixpanel-browser";

/**
 * Utility functions for safe Mixpanel operations
 * These functions check if Mixpanel is properly initialized before making calls
 */

/**
 * Check if Mixpanel is properly initialized
 * @returns {boolean} true if Mixpanel is initialized, false otherwise
 */
export const isMixpanelInitialized = () => {
    try {
        // Check if mixpanel exists and has the required methods
        return mixpanel &&
            typeof mixpanel.identify === 'function' &&
            typeof mixpanel.people === 'object' &&
            typeof mixpanel.people.set === 'function';
    } catch (error) {
        console.warn('Mixpanel not properly initialized:', error);
        return false;
    }
};

/**
 * Safely identify a user in Mixpanel
 * @param {string} userId - The user ID to identify
 */
export const safeIdentify = (userId) => {
    if (!isMixpanelInitialized()) {
        console.warn('Mixpanel not initialized. Skipping identify call.');
        return;
    }

    try {
        mixpanel.identify(userId);
    } catch (error) {
        console.error('Error identifying user in Mixpanel:', error);
    }
};

/**
 * Safely set user properties in Mixpanel
 * @param {Object} properties - The properties to set
 */
export const safeSetUserProperties = (properties) => {
    if (!isMixpanelInitialized()) {
        console.warn('Mixpanel not initialized. Skipping user properties set.');
        return;
    }

    try {
        mixpanel.people.set(properties);
    } catch (error) {
        console.error('Error setting user properties in Mixpanel:', error);
    }
};

/**
 * Safely track an event in Mixpanel
 * @param {string} eventName - The name of the event to track
 * @param {Object} properties - Optional properties for the event
 */
export const safeTrack = (eventName, properties = {}) => {
    if (!isMixpanelInitialized()) {
        console.warn('Mixpanel not initialized. Skipping event tracking.');
        return;
    }

    try {
        mixpanel.track(eventName, properties);
    } catch (error) {
        console.error('Error tracking event in Mixpanel:', error);
    }
};
